'use client';

import { configureStore } from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    createTransform,
    type PersistConfig
} from 'redux-persist';
import { combineReducers } from 'redux';

import popup from '@/hotdesk-ui/store/slices/popup.slice';
import errorModal from '@/hotdesk-ui/store/slices/error.slice';
import geoLocation from '@/hotdesk-ui/store/slices/geo-location.slice';
import search from '@/hotdesk-ui/store/slices/search.slice';
import calculator from '@/hotdesk-ui/store/slices/calculator.slice';
import listingReducer from '@/hotdesk-ui/store/slices/listing.slice';
import currency from '@/hotdesk-ui/store/slices/currency.slice';
import auth from '@/hotdesk-ui/store/slices/auth.slice';
import favorites from '@/hotdesk-ui/store/slices/favorites.slice';
import amenities from '@/hotdesk-ui/store/slices/amenities.slice';
import listingTypes from '@/hotdesk-ui/store/slices/listing-types.slice';
import workspaceCategories from '@/hotdesk-ui/store/slices/workspace-categories.slice';
import fallbackCarousels from '@/hotdesk-ui/store/slices/fallback-carousels.slice';
import suggestedCities from '@/hotdesk-ui/store/slices/suggested-cities.slice';
import expansionRequests from '@/hotdesk-ui/store/slices/expansion-requests.slice';
import compareSelection from '@/hotdesk-ui/store/slices/compare-selection.slice';
import createWebStorage from 'redux-persist/lib/storage/createWebStorage';
import storage from 'redux-persist/lib/storage'; // Defaults to localStorage for web
import bookingReducer from '@/hotdesk-ui/store/slices/booking.slice';

const createNoopStorage = () => {
    return {
        getItem() {
            return Promise.resolve(null);
        },
        setItem(_key: string, value: any) {
            return Promise.resolve(value);
        },
        removeItem() {
            return Promise.resolve();
        }
    };
};
const storageFallback =
    typeof window !== 'undefined' ? storage : createNoopStorage();

// Combine reducers
const rootReducer = combineReducers({
    errorModal,
    geoLocation,
    search,
    calculator,
    listing: listingReducer,
    booking: bookingReducer,
    currency,
    popup,
    auth,
    favorites,
    amenities,
    listingTypes,
    workspaceCategories,
    fallbackCarousels,
    suggestedCities,
    expansionRequests,
    compareSelection
});

// Subscription filtering is opt-in per session: never rehydrate a persisted
// `listing.filters.userSubscriptionIdIn`. It used to default ON, so a stale value
// would otherwise linger in localStorage and keep filtering results on `/` and
// `/workspaces` even though the default filtering was removed.
const stripPersistedSubscriptionFilter = createTransform(
    (inboundState) => inboundState,
    (outboundState: any) => {
        if (outboundState?.filters?.userSubscriptionIdIn) {
            const { userSubscriptionIdIn: _omit, ...restFilters } =
                outboundState.filters;
            return { ...outboundState, filters: restFilters };
        }
        return outboundState;
    },
    { whitelist: ['listing'] }
);

// // Persist configuration
export const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
    key: 'root',
    storage: storageFallback,
    transforms: [stripPersistedSubscriptionFilter],
    // `suggestedCities` is derived straight from Remote Config, which the
    // Firebase SDK already caches. Persisting it only creates a stale copy
    // that outlives config changes — the fetch is skipped whenever the slice
    // already holds cities for the country, so a rehydrated list would pin
    // the old values forever.
    // `compareSelection`: Explore compare mode ends with the page visit, so a
    // rehydrated half-finished pick list would reopen it unasked.
    blacklist: ['suggestedCities', 'compareSelection']
};

// // Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false
        }),
    devTools: process.env.NODE_ENV !== 'production' && {
        name: 'hotdesk-ui',
        trace: true,
        traceLimit: 25
    }
});

// Create persistor only on the client
export const persistor =
    typeof window !== 'undefined' ? persistStore(store) : null;
export const dispatch = store.dispatch;
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
